import React, { FC, useMemo } from 'react';
import { Chips } from 'organisms/Filters/Chips';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useMediaQuery } from 'modules/mui/hooks';
import { Facets } from 'commerce/factFinder/types';
import { FilterItem } from '../FilterItem';
import { useFilters } from '../hooks';

interface AttributeFiltersProps {
  facetType: Facets;
  filterKey: string;
  position?: 'top' | 'bottom';
  withChips?: boolean;
}

const AttributeFilter: FC<AttributeFiltersProps> = ({
  facetType,
  filterKey,
  position,
  withChips = true,
}) => {
  const t = useTranslations();
  const { isMobile } = useMediaQuery();
  const { facets, filters, toggleChips, chipsState, labelMap } = useFilters();

  const { filterItems, filterValues } = useMemo(
    () => ({
      filterItems: facets
        ?.find((facet) => facet.associatedFieldName === facetType)
        ?.elements.map(({ text }) => ({
          type: filterKey,
          value: text,
          displayValue: text,
        })),

      filterValues:
        filters
          ?.flatMap(({ displayValue, type }) =>
            type === filterKey ? [displayValue] : [],
          )
          ?.join(', ') || t(`search.filters.all`),
    }),
    [facets, facetType, filterKey, filters, t],
  );

  const isOpen = chipsState?.filterKey === filterKey;

  return !filterItems ? null : (
    <>
      <FilterItem
        label={labelMap[filterKey]}
        value={filterValues}
        onClick={() => toggleChips?.(filterKey, filterItems, position)}
        isActive={isOpen}
      />
      {isMobile && withChips && isOpen && chipsState?.position === 'bottom' && (
        <Chips isVisible />
      )}
    </>
  );
};

export const Brands: FC = () => (
  <AttributeFilter
    facetType={Facets.BRAND}
    filterKey="brand"
    position="top"
    withChips={false}
  />
);

export const Types: FC = () => (
  <AttributeFilter facetType={Facets.TYPES} filterKey="types" />
);

export const Size: FC = () => (
  <AttributeFilter facetType={Facets.SIZE} filterKey="size" />
);

export const Materials: FC = () => (
  <AttributeFilter facetType={Facets.MATERIALS} filterKey="materials" />
);

export const Genders: FC = () => (
  <AttributeFilter facetType={Facets.GENDERS} filterKey="genders" />
);

export const Tags: FC = () => (
  <AttributeFilter facetType={Facets.TAGS} filterKey="tags" />
);
